import type { LocalViewId } from '@jira/polaris__domain-view/src/ViewKind.types.tsx';
import { isClientFetchError } from '@atlassian/jira-fetch/src/utils/is-error.tsx';
import { experience } from '@jira/polaris__lib-analytics/src/ConstantsExperience.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import type { Props, State } from './NavigationView.types.tsx';
import { withRefreshBlocker } from './withRefreshBlocker.tsx';
import { findView, modifyView } from './updateContainerState.tsx';

export const updateView = (localId: LocalViewId, newName: string): Action<State, Props> =>
	withRefreshBlocker(
		async (
			{ setState, getState },
			{ navigationRemote, cloudId, containerId, onFailure, onSuccess },
		) => {
			if (
				!cloudId ||
				!containerId ||
				!navigationRemote ||
				getState().containers[containerId]?.isLoading ||
				!getState().containers[containerId]?.initialized
			) {
				return;
			}

			const { view } = findView(getState().containers[containerId], localId);

			if (!view || !view.viewType) {
				return;
			}

			experience.navBar.changeViewName.start();

			setState(
				modifyView(getState(), containerId, (innerView) => {
					if (innerView.localId === localId) {
						return {
							...view,
							name: newName,
							isRenaming: false,
							isLocked: true,
						};
					}
					return innerView;
				}),
			);

			try {
				const updatedView = await navigationRemote.updateView({
					id: view.id,
					viewType: view.viewType,
					name: newName,
				});

				setState(
					modifyView(getState(), containerId, (innerView) => {
						if (innerView.localId === localId) {
							return {
								...innerView,
								name: updatedView.name,
								isLocked: false,
							};
						}
						return innerView;
					}),
				);

				onSuccess?.('updateView');

				experience.navBar.changeViewName.success();
				// oxlint-disable-next-line @typescript-eslint/no-explicit-any
			} catch (error: any) {
				setState(
					modifyView(getState(), containerId, (innerView) => {
						if (innerView.localId === localId) {
							return {
								...innerView,
								isLocked: false,
							};
						}
						return innerView;
					}),
				);

				onFailure?.(error, 'updateView');

				if (isClientFetchError(error)) {
					experience.navBar.changeViewName.abort(error);
					return;
				}

				experience.navBar.changeViewName.failure(error);
			}
		},
	);
